import * as Yup from "yup";
import { CustomerType } from "../components/Customers/enums/customerType";
import { validatePhoneWithCountrySelection } from "@kivunova/kivufrontendcommon";

export const buildCustomerSchema = (
  t: (key: string, fallback: string) => string,
  options?: { requireStoreId?: boolean }
) =>
  Yup.object().shape({
    companyId: Yup.string().required(
      t("company-id-required", "Company ID is required")
    ),
    storeId: options?.requireStoreId
      ? Yup.string().required(t("store-required", "Store is required"))
      : Yup.string().optional(),
    name: Yup.string().required(t("name-required", "Name is required")),
    tin: Yup.string().when("type", {
      is: CustomerType.COMPANY,
      then: (schema) =>
        schema.required(
          t("tin-required-for-companies", "TIN is required for companies")
        ),
      otherwise: (schema) => schema.optional()
    }),
    phone: Yup.string()
      .trim()
      .test(
        "phone-validation",
        t("phone-invalid", "Invalid phone number for selected country"),
        function (value) {
          const trimmed = (value ?? "").trim();
          if (!trimmed) return true;
          const { phoneCountryCode } = this.parent;
          return validatePhoneWithCountrySelection(trimmed, phoneCountryCode);
        }
      ),
    phoneCountryCode: Yup.string().when("phone", {
      is: (phone: string) => Boolean(phone && String(phone).trim()),
      then: (schema) =>
        schema.required(t("country-code-required", "Country code is required")),
      otherwise: (schema) => schema.optional()
    }),
    email: Yup.string()
      .trim()
      .test(
        "email-format",
        t("email-invalid", "Invalid email format"),
        (value) => {
          if (!value || !String(value).trim()) return true;
          try {
            return Yup.string().email().isValidSync(value);
          } catch {
            return false;
          }
        }
      )
      .test(
        "phone-or-email",
        t(
          "phone-or-email-required",
          "Provide a phone number or an email address"
        ),
        function (value) {
          const email = (value ?? "").trim();
          const phone = (this.parent.phone ?? "").trim();
          return Boolean(phone || email);
        }
      )
      .optional(),
    type: Yup.mixed<CustomerType>()
      .oneOf(
        Object.values(CustomerType),
        t("customer-type-invalid", "Invalid customer type")
      )
      .required(t("customer-type-required", "Customer type is required")),
    address: Yup.object()
      .shape({
        country: Yup.string().optional().nullable(),
        province: Yup.string().optional().nullable(),
        district: Yup.string().optional().nullable(),
        sector: Yup.string().optional().nullable(),
        cell: Yup.string().optional().nullable(),
        village: Yup.string().optional().nullable(),
        streetAddress: Yup.string().optional().nullable()
      })
      .optional()
      .nullable()
  });
